import React from 'react';
import { motion } from 'framer-motion';
import { 
  Layout, 
  ClipboardList, 
  Calendar, 
  Mail,
  ArrowRight
} from 'lucide-react';

const captureElements = [
  {
    icon: Layout,
    title: 'Landing Pages',
    description: 'Conversion-optimized pages designed to capture attention and drive action.',
    preview: 'High-converting headlines, compelling copy, clear CTAs'
  },
  {
    icon: ClipboardList,
    title: 'Lead Capture Forms',
    description: 'Strategic form design that balances data collection with user experience.',
    preview: 'Progressive profiling, smart fields, instant validation'
  },
  {
    icon: Calendar,
    title: 'Booking Pages',
    description: 'Seamless scheduling that removes friction from the sales process.',
    preview: 'Calendar sync, automated reminders, timezone detection'
  },
  {
    icon: Mail,
    title: 'Email Signup',
    description: 'Value-driven opt-ins that build your audience for long-term nurturing.',
    preview: 'Lead magnets, welcome sequences, segmentation'
  },
];

export default function ConsiderationSection() {
  return (
    <section 
      id="consideration"
      data-testid="consideration-section"
      className="section-padding relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-[#030303]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(59,130,246,0.08)_0%,_transparent_50%)]" />
      
      <div className="container-custom relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-blue-400 mb-4 block">
            Stage 2 / Consideration
          </span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 
              data-testid="consideration-headline"
              className="text-4xl md:text-5xl font-bold text-white"
            >
              Lead
              <br />
              <span className="text-blue-400">Capture</span>
            </h2>
            <p className="text-gray-400 max-w-md text-base">
              Convert anonymous visitors into identified leads through strategic 
              capture points that provide value at every step.
            </p>
          </div>
        </motion.div>
        
        {/* Capture Elements */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {captureElements.map((element, index) => (
            <motion.div
              key={index}
              data-testid={`consideration-card-${index}`}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="group"
            >
              <div className="glass rounded-2xl overflow-hidden card-hover border border-blue-500/10">
                <div className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/20 transition-colors">
                      <element.icon className="w-6 h-6 text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-1">
                        {element.title}
                      </h3>
                      <p className="text-gray-400 text-sm leading-relaxed">
                        {element.description}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Preview bar */}
                <div className="px-6 py-4 bg-blue-500/5 border-t border-blue-500/10">
                  <p className="text-xs text-blue-400/70">
                    {element.preview}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Visual Flow */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-16 text-center"
        >
          <div className="inline-flex items-center gap-4 glass rounded-full px-6 py-3">
            <span className="text-gray-400 text-sm">Visitors</span>
            <ArrowRight className="w-4 h-4 text-blue-400" />
            <span className="text-blue-400 font-medium text-sm">Captured Leads</span>
            <ArrowRight className="w-4 h-4 text-blue-400" />
            <span className="text-gray-400 text-sm">CRM Database</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
